import { Box, Grid, SelectChangeEvent } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { selectAllCities } from "../../../app/appSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CustomerOrgCompanyAutocompleteSingle from "../../../common/components/functional/autocomplete/customerOrgCompanyAutocomplete/CustomerOrgCompanyAutocompleteSingle";
import useCustomerOrgCompanyAutocomplete from "../../../common/components/functional/autocomplete/customerOrgCompanyAutocomplete/useCustomerOrgCompanyAutocomplete";
import AutocompleteBase from "../../../common/components/functional/fields/autocompleteBase/AutocompleteBase";
import CertificateAutocomplete from "../../../common/components/functional/fields/certificatesAutocomplete/CertificatesAutocomplete";
import CitiesAndCountiesAutocomplete from "../../../common/components/functional/fields/citiesAndCountiesAutocomplete/CitiesAndCountiesAutocomplete";
import ExpertsAutocomplete from "../../../common/components/functional/fields/expertsAutocomplete/ExpertsAutocomplete";
import LanguageSkillsAutocomplete from "../../../common/components/functional/fields/languageSkillsAutocomplete/LanguageSkillsAutocomplete";
import RecruitmentAreasAutocomplete from "../../../common/components/functional/fields/recruitmentAreasAutocomplete/RecruitmentAreasAutocomplete";
import CommonSelectField, {
  SelectOption,
} from "../../../common/components/functional/fields/selectField/CommonSelectField";
import SearchTermsField from "../../../common/components/functional/fields/searchTermsField/SearchTermsField";
import TalentSkillsAutocomplete from "../../../common/components/functional/fields/talentSkillsAutocompleteField/TalentSkillsAutocomplete";
import TrainingsAutocomplete from "../../../common/components/functional/fields/trainingsAutocomplete/TrainingsAutocomplete";
import WorkTitleAutocomplete from "../../../common/components/functional/fields/workTitleAutocomplete/WorkTitleAutocomplete";
import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import { RoundedButton } from "../../../common/components/styled/styled";
import { joinNamesByIds, stringToList } from "../../../common/helpers/common";
import {
  idsToLocalizedSkills,
  idsToNameIdOptions,
  isSearchTermsFilter,
  makeLookupValues,
  makeMultiSelectDisplay,
} from "../../../common/helpers/filters";
import {
  isoFormat,
  leadingZerosDateFormat,
  validateDateInput,
} from "../../../common/helpers/formatDateTime";
import { CityAndCounty } from "../../../common/types/apiTypes";
import { NameIdOption } from "../../../common/types/commonTypes";
import {
  defaultNullableDateRange,
  NullableDateRange,
} from "../../../common/types/nullable";
import { WorkTitleOption } from "../../../hooks/useWorkTitleAutocomplete";
import { selectLanguageSkillsCodeNameIndex } from "../../languageSkills/languageSkillsSlice";
import { OrderQueueOption } from "../../settings/orderQueues/types";
import { selectRecruitmentAreaOptions } from "../../settings/recruitment/recruitmentsSlice";
import { fetchOrderQueues } from "../../settings/settingsActions";
import { LocalizedSkill } from "../../talentProfile/LocalizedGroups";
import { TalentTag } from "../../talentTags/talentTagsAPI";
import { selectAllTalentTags } from "../../talentTags/talentTagsSlice";
import {
  ACCOUNTING,
  ADMIN,
  includesAuthScope,
  SALARY_BILLING_HR,
} from "../../user/accessRightChecks";
import { AssignmentType, TalentStatus } from "../types/common";
import TalentFiltersDateRange from "./TalentFiltersDateRange";
import TalentFiltersInfoRow from "./TalentFiltersInfoRow";
import TalentFiltersRow from "./TalentFiltersRow";
import isEmpty from "../../../common/helpers/isEmpty";

interface Props {
  filters: SearchFilter[];
  updateFilters: (filters: SearchFilter[]) => void;
  handleMenuToggle: () => void;
  selectedQuickFilter?: string;
}

const TalentFiltersComponent = ({
  filters,
  updateFilters,
  handleMenuToggle,
  selectedQuickFilter,
}: Props) => {
  const [statuses, setStatuses] = useState<string[]>([]);
  const [selectedExperts, setSelectedExperts] = useState<NameIdOption[]>([]);
  const [selectedTalentTags, setSelectedTalentTags] = useState<string[]>([]);
  const [selectedRecruitmentAreas, setSelectedRecruitmentAreas] = useState<
    NameIdOption[]
  >([]);
  const [selectedQueues, setSelectedQueues] = useState<OrderQueueOption[]>([]);
  const [queueOptions, setQueueOptions] = useState<OrderQueueOption[]>([]);
  const [selectedWorkTitles, setSelectedWorkTitles] = useState<
    WorkTitleOption[]
  >([]);
  const [selectedCities, setSelectedCities] = useState<CityAndCounty[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<LocalizedSkill[]>([]);
  const [selectedCertificates, setSelectedCertificates] = useState<
    NameIdOption[]
  >([]);
  const [selectedTrainings, setSelectedTrainings] = useState<NameIdOption[]>(
    [],
  );
  const [selectedLanguageSkills, setSelectedLanguageSkills] = useState<
    NameIdOption[]
  >([]);
  const [vehicleAvailable, setVehicleAvailable] = useState<string | undefined>(
    "",
  );
  const [searchTermInput, setSearchTermInput] = useState<string>("");
  const [localSearchTerms, setLocalSearchTerms] = useState<string[]>([]);

  const {
    customerOrganization: selectedCustomerOrg,
    onChange: onCustomerOrgChange,
    customerOptions,
    onInputChange: onCustomerInputChange,
    isLoading: isCustomerLoading,
  } = useCustomerOrgCompanyAutocomplete();

  const [selectedAssignmentType, setSelectedAssignmentType] = useState<
    AssignmentType | ""
  >("");
  const [assignmentActiveDates, setAssignmentActiveDates] =
    useState<NullableDateRange>(defaultNullableDateRange);
  const [assignmentInactiveDates, setAssignmentInactiveDates] =
    useState<NullableDateRange>(defaultNullableDateRange);
  const assignmentRowWidth = 300;
  const infoTooltipWidth = 16;
  const infoTooltipGap = 8;

  const talentTagOptions: TalentTag[] = useAppSelector(selectAllTalentTags);
  const allCities = useAppSelector(selectAllCities);
  const { certificate, training } = useAppSelector((state) => state.localized);
  const languageNames = useAppSelector(selectLanguageSkillsCodeNameIndex);

  const { user } = useAppSelector((state) => state.user);

  const recruitmentAreaOptions = useAppSelector(selectRecruitmentAreaOptions);

  const talentStatusFilterOrder = [
    TalentStatus.INCOMPLETE,
    TalentStatus.NEW,
    TalentStatus.INTERVIEW,
    TalentStatus.INTERVIEW_PENDING,
    TalentStatus.ACTIVE,
    TalentStatus.LATER,
    TalentStatus.EMPLOYED_ELSEWHERE,
    TalentStatus.NOT_EMPLOYED,
    TalentStatus.DISABLED,
    TalentStatus.DEACTIVATED,
  ];

  // TalentStatus DISABLED and DEACTIVATED are not available options for all Expert roles.
  const talentStatusOptions = talentStatusFilterOrder
    .filter((status: TalentStatus) => {
      if (
        status === TalentStatus.DISABLED ||
        status === TalentStatus.DEACTIVATED
      ) {
        return (
          user &&
          includesAuthScope(user, [ADMIN, SALARY_BILLING_HR, ACCOUNTING])
        );
      }
      return true;
    })
    .map((status) => ({
      name: status,
      value: status,
    }));

  const { t } = useTranslation(["talentProfile", "common", "errors"]);
  const dispatch = useAppDispatch();

  const ownQueueOption = useMemo(
    () => ({ id: "own", name: t("talentProfile:own"), isTeamQueue: true }),
    [t],
  );

  useEffect(() => {
    dispatch(fetchOrderQueues()).then((action) => {
      if (isFulfilled(action)) {
        setQueueOptions([
          ownQueueOption,
          ...action.payload.sort((a, b) => a.name.localeCompare(b.name, "fi")),
        ]);
      }
    });
  }, [dispatch, ownQueueOption]);

  const findFilter = useCallback(
    (name: string) => {
      return filters.find((filter) => filter.name === name);
    },
    [filters],
  );

  useEffect(() => {
    // Set local filters when filters are updated (removed) from parent
    const statusFilter = findFilter("status");
    if (statusFilter) {
      setStatuses(statusFilter.value || []);
    }

    const expertFilter = findFilter("hrRepresentative");
    if (expertFilter) {
      setSelectedExperts(
        idsToNameIdOptions(
          expertFilter.value || [],
          expertFilter.lookupValues,
        ),
      );
    }

    const tagFilter = findFilter("talentTags");
    if (tagFilter) {
      setSelectedTalentTags(tagFilter.value || []);
    }

    const areaFilter = findFilter("areas");
    if (areaFilter?.value && areaFilter.value.length > 0) {
      setSelectedRecruitmentAreas(
        idsToNameIdOptions(
          areaFilter.value as string[],
          areaFilter.lookupValues,
          recruitmentAreaOptions,
        ),
      );
    } else {
      setSelectedRecruitmentAreas([]);
    }

    const queuesFilter = findFilter("orderQueues");
    if (queuesFilter) {
      const queueIds = queuesFilter.value || [];
      const selectedQueueOptions = queueOptions.filter((queue) =>
        queueIds.includes(queue.id),
      );
      setSelectedQueues(selectedQueueOptions);
    } else {
      setSelectedQueues([]);
    }

    const workTitleFilter = findFilter("workTitleIds");
    if (workTitleFilter?.value && workTitleFilter.value.length > 0) {
      setSelectedWorkTitles(
        idsToNameIdOptions(
          workTitleFilter.value as string[],
          workTitleFilter.lookupValues,
        ) as WorkTitleOption[],
      );
    } else {
      setSelectedWorkTitles([]);
    }

    const cityFilter = findFilter("city");
    if (cityFilter?.value && cityFilter.value.length > 0) {
      const cityOptions = (cityFilter.value as string[])
        .map((cityName) => allCities.find((c) => c.city === cityName))
        .filter(Boolean) as CityAndCounty[];
      setSelectedCities(cityOptions);
    } else {
      setSelectedCities([]);
    }

    const skillsFilter = findFilter("skillIds");
    if (skillsFilter?.value && skillsFilter.value.length > 0) {
      setSelectedSkills(
        idsToLocalizedSkills(
          skillsFilter.value as string[],
          skillsFilter.lookupValues,
        ) as LocalizedSkill[],
      );
    } else {
      setSelectedSkills([]);
    }

    const certificatesFilter = findFilter("certificateIds");
    if (certificatesFilter?.value && certificatesFilter.value.length > 0) {
      setSelectedCertificates(
        certificatesFilter.value.map((id: string) => ({
          id,
          name: certificate[id] || id,
        })),
      );
    } else {
      setSelectedCertificates([]);
    }

    const trainingsFilter = findFilter("trainingIds");
    if (trainingsFilter?.value && trainingsFilter.value.length > 0) {
      setSelectedTrainings(
        trainingsFilter.value.map((id: string) => ({
          id,
          name: training[id] || id,
        })),
      );
    } else {
      setSelectedTrainings([]);
    }

    const languageSkillsFilter = findFilter("languageSkillIds");
    if (languageSkillsFilter?.value && languageSkillsFilter.value.length > 0) {
      setSelectedLanguageSkills(
        languageSkillsFilter.value.map((id: string) => ({
          id,
          name: languageNames[id],
        })),
      );
    } else {
      setSelectedLanguageSkills([]);
    }

    const vehicleAvailableFilter = findFilter("vehicleAvailable");
    if (vehicleAvailableFilter?.value) {
      setVehicleAvailable(vehicleAvailableFilter.value);
    } else {
      setVehicleAvailable(undefined);
    }

    const customerOrgFilter = findFilter("customerOrgId");
    if (customerOrgFilter?.value) {
      const customerOrg = customerOrgFilter.lookupValues?.[0] || null;
      if (customerOrg && customerOrg.id !== selectedCustomerOrg?.id) {
        onCustomerOrgChange({
          id: customerOrg.id,
          name: customerOrg.name,
        } as any);
      }
    } else {
      if (selectedCustomerOrg !== null) {
        onCustomerOrgChange(null);
      }
    }

    const assignmentTypeFilter = findFilter("assignmentType");
    if (assignmentTypeFilter?.value) {
      setSelectedAssignmentType(assignmentTypeFilter.value as AssignmentType);
    } else {
      setSelectedAssignmentType("");
    }

    const assignmentActiveFromFilter = findFilter("assignmentActiveFrom");
    const assignmentActiveToFilter = findFilter("assignmentActiveTo");
    setAssignmentActiveDates({
      from: assignmentActiveFromFilter?.value
        ? new Date(assignmentActiveFromFilter.value as string)
        : null,
      to: assignmentActiveToFilter?.value
        ? new Date(assignmentActiveToFilter.value as string)
        : null,
    });

    const assignmentInactiveFromFilter = findFilter("assignmentInactiveFrom");
    const assignmentInactiveToFilter = findFilter("assignmentInactiveTo");
    setAssignmentInactiveDates({
      from: assignmentInactiveFromFilter?.value
        ? new Date(assignmentInactiveFromFilter.value as string)
        : null,
      to: assignmentInactiveToFilter?.value
        ? new Date(assignmentInactiveToFilter.value as string)
        : null,
    });

    const searchTermsFilter = filters.find(isSearchTermsFilter);
    const terms = searchTermsFilter?.value
      ? Array.isArray(searchTermsFilter.value)
        ? searchTermsFilter.value
        : stringToList(searchTermsFilter.value as string, ",")
      : [];
    setLocalSearchTerms(terms);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    talentTagOptions,
    findFilter,
    allCities,
    certificate,
    languageNames,
    training,
    recruitmentAreaOptions,
    queueOptions,
  ]);

  const updateSelectedExperts = (newValue: NameIdOption[] | null) => {
    setSelectedExperts(newValue || []);
  };

  const updateSelectedWorkTitles = (newValue: WorkTitleOption[] | null) => {
    const normalizedValue = newValue || [];
    setSelectedWorkTitles(normalizedValue);
  };

  const updateSelectedSkills = (newValue: LocalizedSkill[] | null) => {
    setSelectedSkills(newValue || []);
  };

  const updateSelectedCertificates = (newValue: NameIdOption[] | null) => {
    setSelectedCertificates(newValue || []);
  };

  const updateSelectedTrainings = (newValue: NameIdOption[] | null) => {
    setSelectedTrainings(newValue || []);
  };

  const updateSelectedLanguageSkills = (newValue: NameIdOption[] | null) => {
    setSelectedLanguageSkills(newValue || []);
  };

  const updateSelectedRecruitmentAreas = (newValue: NameIdOption[] | null) => {
    setSelectedRecruitmentAreas(newValue || []);
  };

  const handleSearchTermsChange = useCallback((newValue: string[]) => {
    setLocalSearchTerms(newValue);
  }, []);

  const sendFilters = () => {
    const { displayValue: expertsDisplayValue, tooltip: expertsTooltip } =
      makeMultiSelectDisplay(selectedExperts);
    const { displayValue: workTitlesDisplayValue, tooltip: workTitlesTooltip } =
      makeMultiSelectDisplay(selectedWorkTitles);
    const { displayValue: skillsDisplayValue, tooltip: skillsTooltip } =
      makeMultiSelectDisplay(selectedSkills);
    const {
      displayValue: certificatesDisplayValue,
      tooltip: certificatesTooltip,
    } = makeMultiSelectDisplay(selectedCertificates);
    const { displayValue: trainingsDisplayValue, tooltip: trainingsTooltip } =
      makeMultiSelectDisplay(selectedTrainings);
    const {
      displayValue: languageSkillsDisplayValue,
      tooltip: languageSkillsTooltip,
    } = makeMultiSelectDisplay(selectedLanguageSkills);
    const {
      displayValue: recruitmentsDisplayValue,
      tooltip: recruitmentsTooltip,
    } = makeMultiSelectDisplay(selectedRecruitmentAreas);
    const { displayValue: queuesDisplayValue, tooltip: queuesTooltip } =
      makeMultiSelectDisplay(selectedQueues);
    const { displayValue: statusDisplayValue, tooltip: statusTooltip } =
      makeMultiSelectDisplay(
        statuses.map((status) => ({
          name: t(status),
        })),
      );

    const newFilters = [
      {
        name: "status",
        value: statuses,
        displayName: t("talentProfile:statusLabel"),
        displayValue: statusDisplayValue,
        ...(statusTooltip && { tooltip: statusTooltip }),
      },
      {
        name: "hrRepresentative",
        value: selectedExperts.map((expert) => expert.id),
        displayName: t("myHr"),
        displayValue: expertsDisplayValue,
        ...(expertsTooltip && { tooltip: expertsTooltip }),
        lookupValues: makeLookupValues(selectedExperts),
      },
      {
        name: "talentTags",
        value: selectedTalentTags,
        displayName: t("talentProfile:talentTag"),
        displayValue: joinNamesByIds(selectedTalentTags, talentTagOptions),
      },
      {
        name: "areas",
        value: selectedRecruitmentAreas.map((area) => area.id),
        displayName: t("common:area"),
        displayValue: recruitmentsDisplayValue,
        ...(recruitmentsTooltip && { tooltip: recruitmentsTooltip }),
        lookupValues: makeLookupValues(selectedRecruitmentAreas),
      },
      {
        name: "orderQueues",
        value: selectedQueues.map((queue) => queue.id),
        displayName: t("order:team"),
        displayValue: queuesDisplayValue,
        ...(queuesTooltip && { tooltip: queuesTooltip }),
      },
      {
        name: "workTitleIds",
        value: selectedWorkTitles.map((workTitle) => workTitle.id),
        displayName: t("workTitle"),
        displayValue: workTitlesDisplayValue,
        ...(workTitlesTooltip && { tooltip: workTitlesTooltip }),
        lookupValues: makeLookupValues(selectedWorkTitles),
      },
      {
        name: "skillIds",
        value: selectedSkills.map((skill) => skill.id),
        displayName: t("skills"),
        displayValue: skillsDisplayValue,
        ...(skillsTooltip && { tooltip: skillsTooltip }),
        lookupValues: makeLookupValues(selectedSkills),
      },
      {
        name: "city",
        value: selectedCities.map((city) => city.city),
        displayName: t("workHistory:city"),
        displayValue:
          selectedCities.length > 0
            ? selectedCities.map((city) => city.city).join(", ")
            : "",
      },
      {
        name: "certificateIds",
        value: selectedCertificates.map((cert) => cert.id),
        displayName: t("talentProfile:workPermitsTitle"),
        displayValue: certificatesDisplayValue,
        ...(certificatesTooltip && { tooltip: certificatesTooltip }),
      },
      {
        name: "trainingIds",
        value: selectedTrainings.map((training) => training.id),
        displayName: t("common:trainings"),
        displayValue: trainingsDisplayValue,
        ...(trainingsTooltip && { tooltip: trainingsTooltip }),
      },
      {
        name: "languageSkillIds",
        value: selectedLanguageSkills.map((lang) => lang.id),
        displayName: t("talentProfile:languageSkills"),
        displayValue: languageSkillsDisplayValue,
        ...(languageSkillsTooltip && { tooltip: languageSkillsTooltip }),
      },
      {
        name: "vehicleAvailable",
        value: vehicleAvailable,
        displayName: t("talentProfile:vehicleAvailableShort"),
        displayValue:
          vehicleAvailable === "true"
            ? t("common:yes")
            : vehicleAvailable === "false"
              ? t("common:no")
              : "",
      },
      {
        name: "customerOrgId",
        value: selectedCustomerOrg?.id || undefined,
        displayName: t("talentProfile:customer"),
        displayValue: selectedCustomerOrg?.name || "",
        lookupValues: selectedCustomerOrg
          ? [{ id: selectedCustomerOrg.id, name: selectedCustomerOrg.name }]
          : undefined,
      },
      {
        name: "assignmentType",
        value: selectedAssignmentType || undefined,
        displayName: t("talentProfile:assignmentType"),
        displayValue:
          selectedAssignmentType === AssignmentType.LONG
            ? t("talentProfile:longAssignments")
            : selectedAssignmentType === AssignmentType.SHORT
              ? t("talentProfile:shortAssignments")
              : "",
      },
      {
        name: "assignmentActiveFrom",
        value: assignmentActiveDates.from
          ? format(assignmentActiveDates.from, isoFormat)
          : undefined,
        displayName: `${t("talentProfile:assignmentActive")} ${t("talentProfile:from").toLowerCase()}`,
        displayValue: assignmentActiveDates.from
          ? format(assignmentActiveDates.from, leadingZerosDateFormat)
          : "",
      },
      {
        name: "assignmentActiveTo",
        value: assignmentActiveDates.to
          ? format(assignmentActiveDates.to, isoFormat)
          : undefined,
        displayName: `${t("talentProfile:assignmentActive")} ${t("talentProfile:to").toLowerCase()}`,
        displayValue: assignmentActiveDates.to
          ? format(assignmentActiveDates.to, leadingZerosDateFormat)
          : "",
      },
      {
        name: "assignmentInactiveFrom",
        value: assignmentInactiveDates.from
          ? format(assignmentInactiveDates.from, isoFormat)
          : undefined,
        displayName: `${t("talentProfile:assignmentInactive")} ${t("talentProfile:from").toLowerCase()}`,
        displayValue: assignmentInactiveDates.from
          ? format(assignmentInactiveDates.from, leadingZerosDateFormat)
          : "",
      },
      {
        name: "assignmentInactiveTo",
        value: assignmentInactiveDates.to
          ? format(assignmentInactiveDates.to, isoFormat)
          : undefined,
        displayName: `${t("talentProfile:assignmentInactive")} ${t("talentProfile:to").toLowerCase()}`,
        displayValue: assignmentInactiveDates.to
          ? format(assignmentInactiveDates.to, leadingZerosDateFormat)
          : "",
      },
      ...(!isEmpty(localSearchTerms)
        ? [
            {
              name: "searchTerms",
              value: localSearchTerms,
              displayName: t("common:searchTerm"),
            },
          ]
        : []),
    ];
    handleMenuToggle();
    updateFilters(newFilters);
  };

  const statusOptions: SelectOption[] = talentStatusOptions.map((option) => ({
    value: option.value,
    label: t(option.value),
  }));
  const talentTagOptionsAsSelectOption: SelectOption[] = talentTagOptions.map(
    (option) => ({ value: option.id, label: option.name }),
  );
  const vehicleAvailableOptions: SelectOption[] = [
    { value: "", label: t("common:noSelection") },
    { value: "true", label: t("common:yes") },
    { value: "false", label: t("common:no") },
  ];

  const assignmentTypeOptions: SelectOption[] = [
    { value: "", label: t("common:noSelection") },
    { value: AssignmentType.LONG, label: t("talentProfile:longAssignments") },
    { value: AssignmentType.SHORT, label: t("talentProfile:shortAssignments") },
  ];

  const hasActiveRangeError: boolean = !!(
    assignmentActiveDates.from &&
    assignmentActiveDates.to &&
    assignmentActiveDates.from > assignmentActiveDates.to
  );

  const hasInactiveRangeError: boolean = !!(
    assignmentInactiveDates.from &&
    assignmentInactiveDates.to &&
    assignmentInactiveDates.from > assignmentInactiveDates.to
  );

  const hasInvalidActiveDate: boolean =
    (assignmentActiveDates.from !== null &&
      !validateDateInput(assignmentActiveDates.from, true)) ||
    (assignmentActiveDates.to !== null &&
      !validateDateInput(assignmentActiveDates.to, true));

  const hasInvalidInactiveDate: boolean =
    (assignmentInactiveDates.from !== null &&
      !validateDateInput(assignmentInactiveDates.from, true)) ||
    (assignmentInactiveDates.to !== null &&
      !validateDateInput(assignmentInactiveDates.to, true));

  const hasErrors =
    hasActiveRangeError ||
    hasInactiveRangeError ||
    hasInvalidActiveDate ||
    hasInvalidInactiveDate;

  return (
    <Box padding="0px 6px 6px 6px">
      <Grid container>
        <TalentFiltersRow
          label={t("common:searchTerms")}
          labelMinWidth="90px"
          BoxProps={{ mb: 2, mt: 1 }}
        >
          <SearchTermsField
            value={localSearchTerms}
            onChange={handleSearchTermsChange}
            inputValue={searchTermInput}
            onInputChange={setSearchTermInput}
          />
        </TalentFiltersRow>
        <TalentFiltersInfoRow />
        <Grid item container justifyItems="center">
          <Grid item xs={5.5} alignItems="center">
            <TalentFiltersRow label={t("statusLabel")}>
              <CommonSelectField
                multiple
                fullWidth
                showChips
                value={statuses}
                onChange={(event: SelectChangeEvent<string | string[]>) => {
                  const val = event.target?.value;
                  setStatuses(Array.isArray(val) ? val : []);
                }}
                options={statusOptions}
                inputSx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("common:area")}>
              <RecruitmentAreasAutocomplete
                multiple
                fullWidth
                value={selectedRecruitmentAreas}
                onChange={updateSelectedRecruitmentAreas}
                limitTags={2}
                sx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("order:team")}>
              <Box display="flex" gap="8px" width="100%" maxWidth="300px">
                <AutocompleteBase<OrderQueueOption>
                  value={selectedQueues}
                  onChange={(newValue) => setSelectedQueues(newValue || [])}
                  options={queueOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  multiple
                  fullWidth
                  limitTags={2}
                  size="small"
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  sx={{ maxWidth: "300px" }}
                />
                <InfoTooltip
                  style={{ marginTop: "12px", width: "16px" }}
                  title={t("talentProfile:orderQueueFilterTooltip")}
                />
              </Box>
            </TalentFiltersRow>
            <TalentFiltersRow label={t("myHr")}>
              <ExpertsAutocomplete
                value={selectedExperts}
                onChange={updateSelectedExperts}
                includeNoHrRep
                limitTags={2}
                sx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("talentTag")}>
              <CommonSelectField
                multiple
                fullWidth
                showChips
                value={selectedTalentTags}
                onChange={(event: SelectChangeEvent<string | string[]>) => {
                  const val = event.target?.value;
                  setSelectedTalentTags(Array.isArray(val) ? val : []);
                }}
                options={talentTagOptionsAsSelectOption}
                inputSx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("workHistory:city")}>
              <CitiesAndCountiesAutocomplete
                multiple
                value={selectedCities}
                onChange={setSelectedCities}
                limitTags={2}
                sx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("talentProfile:customer")}>
              <Box display="flex" gap="8px" width="100%" maxWidth="300px">
                <CustomerOrgCompanyAutocompleteSingle
                  options={customerOptions}
                  value={selectedCustomerOrg}
                  onChange={onCustomerOrgChange}
                  onInputChange={onCustomerInputChange}
                  isLoading={isCustomerLoading}
                />
                <InfoTooltip
                  style={{ marginTop: "12px", width: "16px" }}
                  title={t("talentProfile:customerFilterInfo")}
                />
              </Box>
            </TalentFiltersRow>
            <TalentFiltersRow
              label={t("talentProfile:assignmentType")}
              labelMaxWidth="90px"
            >
              <CommonSelectField
                value={selectedAssignmentType || ""}
                onChange={(event: SelectChangeEvent<string | string[]>) => {
                  const val = event.target?.value as AssignmentType | "";
                  setSelectedAssignmentType(val || "");
                }}
                options={assignmentTypeOptions}
                fullWidth
                inputSx={{ width: "300px" }}
              />
            </TalentFiltersRow>
          </Grid>
          <Grid item xs={6.5}>
            <TalentFiltersRow label={t("workTitle")}>
              <WorkTitleAutocomplete
                value={selectedWorkTitles}
                onChange={updateSelectedWorkTitles}
                multiple
                fullWidth
                limitTags={2}
                groupByFieldOfWork
                fetchAll
                sx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("skills")}>
              <TalentSkillsAutocomplete
                multiple
                fullWidth
                value={selectedSkills}
                onChange={updateSelectedSkills}
                sx={{ maxWidth: "300px" }}
                limitTags={2}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("talentProfile:workPermitsTitle")}>
              <CertificateAutocomplete
                multiple
                fullWidth
                limitTags={2}
                value={selectedCertificates}
                onChange={updateSelectedCertificates}
                sx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("trainings:trainings")}>
              <TrainingsAutocomplete
                multiple
                fullWidth
                limitTags={2}
                value={selectedTrainings}
                onChange={updateSelectedTrainings}
                sx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("common:languageSkills")}>
              <LanguageSkillsAutocomplete
                multiple
                fullWidth
                limitTags={2}
                value={selectedLanguageSkills}
                onChange={updateSelectedLanguageSkills}
                sx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow label={t("talentProfile:vehicleAvailableShort")}>
              <CommonSelectField
                value={vehicleAvailable || ""}
                onChange={(event: SelectChangeEvent<string | string[]>) => {
                  const val = event.target?.value as string;
                  if (val === "") setVehicleAvailable(undefined);
                  else setVehicleAvailable(val === "true" ? "true" : "false");
                }}
                options={vehicleAvailableOptions}
                fullWidth
                inputSx={{ maxWidth: "300px" }}
              />
            </TalentFiltersRow>
            <TalentFiltersRow
              label={t("talentProfile:assignmentActive")}
              alignItems="flex-start"
            >
              <TalentFiltersDateRange
                value={assignmentActiveDates}
                onChange={setAssignmentActiveDates}
                fromPlaceholder={t("talentProfile:from")}
                toPlaceholder={t("talentProfile:to")}
                tooltipText={t("talentProfile:assignmentActiveFilterInfo")}
                errorMessage={
                  hasActiveRangeError
                    ? t("errors:startDateMustBeBeforeEndDate")
                    : hasInvalidActiveDate
                      ? t("common:invalidDate")
                      : undefined
                }
                rowWidth={assignmentRowWidth}
                tooltipWidth={infoTooltipWidth}
                tooltipGap={infoTooltipGap}
              />
            </TalentFiltersRow>

            <TalentFiltersRow
              label={t("talentProfile:assignmentInactive")}
              alignItems="flex-start"
            >
              <TalentFiltersDateRange
                value={assignmentInactiveDates}
                onChange={setAssignmentInactiveDates}
                fromPlaceholder={t("talentProfile:from")}
                toPlaceholder={t("talentProfile:to")}
                tooltipText={t("talentProfile:assignmentInactiveFilterInfo")}
                errorMessage={
                  hasInactiveRangeError
                    ? t("errors:startDateMustBeBeforeEndDate")
                    : hasInvalidInactiveDate
                      ? t("common:invalidDate")
                      : undefined
                }
                rowWidth={assignmentRowWidth}
                tooltipWidth={infoTooltipWidth}
                tooltipGap={infoTooltipGap}
              />
            </TalentFiltersRow>
          </Grid>
        </Grid>
        <Grid item xs={12} mt={2}>
          <Box display="flex" justifyContent="start">
            <RoundedButton
              variant="contained"
              onClick={sendFilters}
              disabled={hasErrors}
            >
              {t("common:search")}
            </RoundedButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const TalentFilters = memo(TalentFiltersComponent);
export default TalentFilters;
